/**
 * FT-05: Fleet-Wide Crew Hook
 *
 * Stores crew data at organization level (not per-vessel).
 * Uses noScope: true to bypass vessel ID suffixing.
 *
 * Key: 'fleet_crew' (org-scoped, syncs to sr.fleet_crew.v1)
 *
 * Interface matches useCrewDomain for seamless integration.
 */

import React, { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { usePermissions } from '../../usePermissions';
import { useVessel } from '../../../contexts/VesselContext';  // FT-05: For pre-memoized vesselCrew
import { devLog } from '../../../utils/devLog';
import { v4 as uuidv4 } from 'uuid';
import { getOrgId } from '../../../utils/getOrgId';  // FT-168: Stamp organizationId on records

// RBAC helper
const canDo = (can, resource, action) => {
  if (!can) return false;
  try {
    return can(action, resource);
  } catch {
    return false;
  }
};

// FT-05 Commit 8: Per-vessel duty status check
// Uses dutyStatus map: { [vesselId]: 'on_duty' | 'off_duty' }
// Members without dutyStatus entry for vessel default to OFF-duty
const isOnDuty = (member, vesselId) => {
  if (!member || !vesselId) return false;
  const dutyStatus = member.dutyStatus || {};
  return dutyStatus[vesselId] === 'on_duty';
};

// Legacy status normalization: treat 'on duty' as 'active' for lifecycle
const normalizeLegacyStatus = (status) => {
  if (!status) return 'active';
  const s = status.toLowerCase();
  // Legacy 'on duty' / 'onboard' → treat as 'active' for lifecycle
  if (s === 'on duty' || s === 'onboard') return 'active';
  return s;
};

// Normalize status to lowercase (lifecycle only: active/archived)
const normStatus = (status) => {
  return normalizeLegacyStatus(status);
};

// Normalize crew input (same as useCrewDomain)
// FT-168: Always stamp organizationId to prevent cross-org contamination
const normalizeCrewInput = (input) => {
  const now = Date.now();
  const currentOrgId = getOrgId();
  return {
    id: input.id || uuidv4(),
    name: input.name || 'Unknown',
    rank: input.rank || input.position || '',
    position: input.position || input.rank || '',
    status: normStatus(input.status || 'active'),  // Lifecycle only: active/archived
    certificates: input.certificates || [],
    notes: input.notes || '',
    email: input.email || '',
    phone: input.phone || '',
    createdAt: input.createdAt || now,
    createdBy: input.createdBy || 'Unknown User',
    updatedAt: now,
    updatedBy: input.updatedBy || input.createdBy || 'Unknown User',
    // Fleet-specific fields
    assignedVessels: input.assignedVessels || [],
    homeVessel: input.homeVessel || null,
    // FT-05 Commit 8: Per-vessel duty status map
    dutyStatus: input.dutyStatus || {},
    // FT-168: Organization stamp (prevents cross-org contamination)
    organizationId: input.organizationId || currentOrgId
  };
};

// Normalize crew record (same as useCrewDomain)
// FT-168: Preserve organizationId if present
const normalizeCrewRecord = (record) => {
  if (!record || typeof record !== 'object') return null;
  return {
    ...record,
    id: record.id || uuidv4(),
    name: record.name || 'Unknown',
    rank: record.rank || record.position || '',
    position: record.position || record.rank || '',
    status: normStatus(record.status),  // Lifecycle only: active/archived
    certificates: record.certificates || [],
    assignedVessels: record.assignedVessels || [],
    homeVessel: record.homeVessel || null,
    // FT-05 Commit 8: Per-vessel duty status map
    dutyStatus: record.dutyStatus || {},
    // FT-168: Preserve organizationId (don't overwrite with current org on read)
    organizationId: record.organizationId
  };
};

// Filter out wrapper records
const isWrapperRecord = (item) => {
  if (!item || typeof item !== 'object') return true;
  if (item.data && item.data._isArray) return true;
  if (item.data && Array.isArray(item.data.items)) return true;
  if (item.id && typeof item.id === 'string' && /^[0-9a-f-]{36}_/.test(item.id)) return true;
  return false;
};

// Normalize legacy data
const normalizeLegacy = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(r => !isWrapperRecord(r)).map(normalizeCrewRecord).filter(Boolean);
};

/**
 * useFleetCrew - Organization-wide crew roster
 *
 * FT-05: Uses noScope: true to store at org level
 * Same interface as useCrewDomain for seamless migration
 */
export const useFleetCrew = () => {
  // Version marker for dev tracking
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      devLog('[useFleetCrew] v2026-03-22 - FT-05 Fleet-wide crew (org-scoped)');
    }
  }, []);

  // FT-05: noScope: true = org-scoped storage (no vessel suffix)
  const { value: raw = [], setValue } = useStorageState('fleet_crew', [], { noScope: true });
  const { can, currentUser } = usePermissions();
  const userName = currentUser?.name || 'Unknown User';

  // FT-05: Get currentVesselId for pre-memoized vesselCrew
  const { currentVesselId } = useVessel();

  // FT-175: Normalize on read and filter out tombstoned records
  // Records with deletedAt are soft-deleted (tombstones for sync)
  const list = useMemo(() => {
    const normalized = normalizeLegacy(raw);
    return normalized.filter(m => !m.deletedAt);
  }, [raw]);

  // FT-175: Get all records including deleted (for admin/restore purposes)
  const listWithDeleted = useMemo(() => normalizeLegacy(raw), [raw]);

  // FT-05: Pre-memoized vessel crew — stable reference, prevents infinite loops in consumers
  // This is the PRIMARY data that most consumers will use
  const vesselCrew = useMemo(() => {
    if (!currentVesselId) return [];
    return list.filter(m =>
      m.status !== 'archived' &&
      (m.homeVessel === currentVesselId || (m.assignedVessels || []).includes(currentVesselId))
    );
  }, [list, currentVesselId]);

  // Add new crew member - RBAC-gated
  const add = useCallback((input = {}) => {
    if (!canDo(can, 'crew', 'create')) {
      devLog('[useFleetCrew] Permission denied: create');
      return null;
    }

    const member = normalizeCrewInput({ ...input, createdBy: userName });

    if (process.env.NODE_ENV !== 'production') {
      devLog('[useFleetCrew] Adding member:', member.name);
    }

    setValue(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return [...current, member];
    });

    return member;
  }, [can, setValue, userName]);

  // Update existing crew member - RBAC-gated
  const update = useCallback((id, updates) => {
    if (!canDo(can, 'crew', 'update')) {
      devLog('[useFleetCrew] Permission denied: update');
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => m.id === id
        ? { ...m, ...updates, updatedAt: now, updatedBy: userName }
        : m
      );
    });

    return true;
  }, [can, setValue, userName]);

  // Archive crew member (soft delete) - RBAC-gated
  const archive = useCallback((id) => {
    if (!canDo(can, 'crew', 'delete')) {
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => m.id === id
        ? { ...m, status: 'archived', updatedAt: now }
        : m
      );
    });

    return true;
  }, [can, setValue]);

  // FT-175: Restore crew member - clears tombstone fields AND sets status to active
  // Can restore both archived (status=archived) and deleted (deletedAt set) records
  const restore = useCallback((id) => {
    if (!canDo(can, 'crew', 'update')) {
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => m.id === id
        ? {
            ...m,
            status: 'active',
            // FT-175: Clear tombstone fields on restore
            deletedAt: null,
            deletedBy: null,
            _hardDelete: false,
            updatedAt: now,
            updatedBy: userName
          }
        : m
      );
    });

    devLog(`[useFleetCrew] FT-175: Restored crew member ${id}`);
    return true;
  }, [can, setValue, userName]);

  // FT-175: Delete crew member - now uses tombstone pattern
  // Records are marked with deletedAt instead of being removed from array
  // This allows sync to propagate deletions properly
  // @param id - ID of the crew member to delete
  // @param hardDelete - if true, marks for permanent deletion (no restore)
  const remove = useCallback((id, hardDelete = false) => {
    if (!canDo(can, 'crew', 'delete')) {
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => m.id === id
        ? {
            ...m,
            deletedAt: now,
            deletedBy: userName,
            _hardDelete: hardDelete,  // Flag for future cloud cleanup
            updatedAt: now,
            updatedBy: userName
          }
        : m
      );
    });

    devLog(`[useFleetCrew] FT-175: Tombstoned crew member ${id} (hardDelete: ${hardDelete})`);
    return true;
  }, [can, setValue, userName]);

  // Get single crew member by ID
  const getById = useCallback((id) => {
    return list.find(m => m.id === id) || null;
  }, [list]);

  // Get active crew only
  const getActive = useCallback(() => {
    return list.filter(m => (m.status || 'active').toLowerCase() === 'active');
  }, [list]);

  // Get archived crew only
  const getArchived = useCallback(() => {
    return list.filter(m => m.status === 'archived');
  }, [list]);

  // Get onboard crew (backwards compat)
  const getOnboard = useCallback(() => {
    return list.filter(m => isOnDuty(m) && m.status !== 'archived');
  }, [list]);

  // Toggle onboard status (backwards compat)
  const toggleOnboard = useCallback((id) => {
    if (!canDo(can, 'crew', 'update')) {
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => m.id === id
        ? { ...m, onboard: !m.onboard, updatedAt: now, updatedBy: userName }
        : m
      );
    });

    return true;
  }, [can, setValue, userName]);

  // FT-05 Commit 8: Toggle duty status for a specific vessel
  // Writes to dutyStatus[vesselId] map, not flat status field
  const toggleDutyStatus = useCallback((id, vesselId) => {
    if (!canDo(can, 'crew', 'update')) {
      return false;
    }
    if (!vesselId) {
      devLog('[useFleetCrew] toggleDutyStatus: vesselId required');
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => {
        if (m.id !== id) return m;
        const currentDutyStatus = m.dutyStatus || {};
        const currentlyOnDuty = currentDutyStatus[vesselId] === 'on_duty';
        return {
          ...m,
          dutyStatus: {
            ...currentDutyStatus,
            [vesselId]: currentlyOnDuty ? 'off_duty' : 'on_duty'
          },
          updatedAt: now,
          updatedBy: userName
        };
      });
    });

    return true;
  }, [can, setValue, userName]);

  // FT-05 Commit 8: Get all crew who are On Duty for a specific vessel
  const getOnDuty = useCallback((vesselId) => {
    if (!vesselId) return [];
    return list.filter(m => isOnDuty(m, vesselId) && m.status !== 'archived');
  }, [list]);

  // FT-05: Get crew assigned to a specific vessel
  const getByVessel = useCallback((vesselId) => {
    if (!vesselId) return [];
    return list.filter(m =>
      m.status !== 'archived' &&
      (m.homeVessel === vesselId || (m.assignedVessels || []).includes(vesselId))
    );
  }, [list]);

  // FT-05: Assign crew to vessel
  const assignToVessel = useCallback((crewId, vesselId) => {
    if (!canDo(can, 'crew', 'update')) {
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => {
        if (m.id !== crewId) return m;
        const vessels = new Set(m.assignedVessels || []);
        vessels.add(vesselId);
        return {
          ...m,
          assignedVessels: Array.from(vessels),
          updatedAt: now,
          updatedBy: userName
        };
      });
    });

    return true;
  }, [can, setValue, userName]);

  // FT-05: Remove crew from vessel
  const removeFromVessel = useCallback((crewId, vesselId) => {
    if (!canDo(can, 'crew', 'update')) {
      return false;
    }

    const now = Date.now();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(m => {
        if (m.id !== crewId) return m;
        const vessels = (m.assignedVessels || []).filter(v => v !== vesselId);
        return {
          ...m,
          assignedVessels: vessels,
          homeVessel: m.homeVessel === vesselId ? null : m.homeVessel,
          updatedAt: now,
          updatedBy: userName
        };
      });
    });

    return true;
  }, [can, setValue, userName]);

  // FT-175: Get deleted (tombstoned) records for admin/restore view
  const getDeleted = useCallback(() => {
    return listWithDeleted.filter(m => m.deletedAt);
  }, [listWithDeleted]);

  // Memoized return
  return useMemo(() => ({
    // Data
    list,                    // All org crew (excludes tombstoned)
    listWithDeleted,         // FT-175: All crew including tombstoned (for admin)
    vesselCrew,  // FT-05: Pre-memoized crew for current vessel (prevents infinite loops)

    // CRUD operations (RBAC-gated)
    add,
    update,
    archive,
    restore,
    remove,

    // Query helpers
    getById,
    getActive,
    getArchived,
    getDeleted,      // FT-175: Get tombstoned records
    getOnboard,      // Backwards compat
    toggleOnboard,   // Backwards compat

    // Duty status
    getOnDuty,
    toggleDutyStatus,

    // FT-05: Fleet-specific operations
    getByVessel,
    assignToVessel,
    removeFromVessel,

    // FT-05 Commit 8: Computed values - duty counts are now per-vessel
    totalCrew: list.length,
    activeCrew: list.filter(m => m.status !== 'archived').length,  // Lifecycle: non-archived
    archivedCrew: list.filter(m => m.status === 'archived').length,
    // Per-vessel duty counts (use currentVesselId)
    onboardCrew: vesselCrew.filter(m => isOnDuty(m, currentVesselId)).length,  // Backwards compat
    onDutyCrew: vesselCrew.filter(m => isOnDuty(m, currentVesselId)).length,
    offDutyCrew: vesselCrew.filter(m => !isOnDuty(m, currentVesselId)).length,
    vesselCrewCount: vesselCrew.length,  // FT-05: Count of crew assigned to current vessel

    // FT-05 Commit 8: Export isOnDuty helper for consumers
    currentVesselId,  // Export so consumers can use it
    isOnDuty  // Export helper function
  }), [
    list, listWithDeleted, vesselCrew, currentVesselId, add, update, archive, restore, remove,
    getById, getActive, getArchived, getDeleted, getOnboard, toggleOnboard,
    getOnDuty, toggleDutyStatus,
    getByVessel, assignToVessel, removeFromVessel
  ]);
};

export default useFleetCrew;
